import React from 'react';

import { useStyles } from '~/assets/styles';
import { useCurrencyFormatter } from '~/components/CurrencyFormatterContext';
import { ShowOnlyTextField } from '~/components/TextFieldFormik';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import { serverDateToLocal } from '~/DateTimeUtils';
import { formatExchangeRate, getPolicyCurrency } from '~/Utils/currencyUtils';

interface CurrencyConversionDisplayProps {
  amount?: number | string;
  conversionRate?: number | string;
  conversionDate?: string | Date;
  requestCurrency?: string;
  claim?: ClaimPolymorph;
  doneDisplayingConversionDate?: boolean;
}

const CurrencyConversionDisplay: React.FC<CurrencyConversionDisplayProps> = ({
  amount,
  conversionRate,
  conversionDate,
  doneDisplayingConversionDate,
  requestCurrency,
  claim,
}) => {
  const { classes } = useStyles();
  const { currencyFormatter } = useCurrencyFormatter();

  // Validation checks
  if (!amount || !conversionRate) {
    return null;
  }

  const policyCurrency = getPolicyCurrency(claim);

  // Only show if using a different currency than policy currency
  if (requestCurrency === policyCurrency) {
    return null;
  }

  // Parse and validate input values
  const parsedAmount = parseFloat(amount.toString());
  const parsedConversionRate = parseFloat(conversionRate.toString());

  if (isNaN(parsedAmount) || isNaN(parsedConversionRate) || parsedConversionRate === 0) {
    return null;
  }

  // Calculate the amount in base currency
  const baseAmount = parsedAmount / parsedConversionRate;
  const exchangeDate = conversionDate ? serverDateToLocal(conversionDate) : '';

  return (
    <div className="flex w-full flex-row gap-12">
      <div className="w-[120px]">
        <ShowOnlyTextField
          classes={classes}
          label="Original amount"
          showOnlyValueComponent={currencyFormatter.format(baseAmount, policyCurrency)}
        />
      </div>
      <div className="w-[120px]">
        <ShowOnlyTextField
          classes={classes}
          label="Conversion rate"
          showOnlyValueComponent={formatExchangeRate(parsedConversionRate)}
        />
      </div>
      {!doneDisplayingConversionDate && (
        <div className="w-[120px]">
          <ShowOnlyTextField classes={classes} label="Conversion date" showOnlyValueComponent={exchangeDate} />
        </div>
      )}
    </div>
  );
};

export default CurrencyConversionDisplay;
